import * as userServices from '@src/services/user';

type UserLabelMap = Record<number, string>;

const cache: UserLabelMap = {};

function toPositiveIds(raw: unknown[]): number[] {
  const ids = raw
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n) && n > 0);
  return Array.from(new Set(ids));
}

function pickUsername(user: any): string {
  return String(user?.username || user?.name || '').trim();
}

function formatUserLabel(id: number, username?: string | null): string {
  const name = String(username || '').trim();
  if (name) return `${name} (#${id})`;
  return `#${id}`;
}

/**
 * Resolve user ids → "username (#id)" via GET /user?queryInput={id:[...]}&page=...
 * Module cache so detail reload / multiple fields don't refetch.
 */
export async function resolveUserLabels(
  ids: Array<number | string | null | undefined>,
  pageId?: number | string | null
): Promise<UserLabelMap> {
  const allIds = toPositiveIds(ids as unknown[]);
  const missing = allIds.filter((id) => cache[id] == null);
  if (!missing.length) {
    return { ...cache };
  }

  try {
    const params: Record<string, any> = {
      queryInput: { id: missing },
      limit: missing.length,
      skip: 0,
    };
    if (pageId != null && pageId !== '') {
      params.page = pageId;
      params.api = 'list';
    }
    const res: any = await userServices.getList(params as any);
    const list = res?.data?.data || [];
    if (Array.isArray(list)) {
      list.forEach((u: any) => {
        const id = Number(u?.id);
        if (!Number.isFinite(id) || id <= 0) return;
        cache[id] = formatUserLabel(id, pickUsername(u));
      });
    }
  } catch {
    // ignore — fallback #id below
  }

  missing.forEach((id) => {
    if (cache[id] == null) cache[id] = formatUserLabel(id);
  });

  return { ...cache };
}

export function formatUserById(
  id: number | string | null | undefined,
  map: UserLabelMap,
  empty = '—'
): string {
  if (id == null || id === '') return empty;
  const n = Number(id);
  if (!Number.isFinite(n) || n <= 0) return empty;
  return map[n] || formatUserLabel(n);
}
